import { IBaseGrid } from "Grids/Interfaces";
import { MappingDisplayType, TemplateTypes } from "Enums";
import { ServiceProvider } from "Service";
import { Configuration } from "Configuration";
import { GridEventTypes } from "Core/Events/EventTypes";
import { IDisposable } from "Core/Interfaces";
import { EventArgs } from "Core/Events";
import { AppContext, QueryParameter } from "Core";
import { UserGridConfigExtension } from "Grids/Extension/UserGridConfigExtension";
import { MappingDefinition, BaseGridDefinition, GridTypes, Environment } from "Service/Client";

/**
 * ## Mapping
 *
 * Represents a single loaded mapping on the page.
 * @since 4.1.0
 */
export class Mapping implements IDisposable {
    private grids: IBaseGrid[];
    private mappingName: string;
    private serviceProvider: ServiceProvider;
    private textBlockCategory: string;
    private height: number;

    private mappingDefinition: MappingDefinition;
    private container: JQuery;

    private userGridConfigExtension: UserGridConfigExtension;

    private globalInitializePromise: Promise<void>;

    /**
     * Create a new mapping.
     * @param mappingName
     * @param textBlockCategory
     * @param serviceProvider
     * @hidden
     */
    constructor(
        mappingName: string,
        textBlockCategory: string,
        serviceProvider: ServiceProvider,
        globalInitializePromise: Promise<void>,
        private environment: Environment
    ) {
        this.mappingName = mappingName;
        this.textBlockCategory = textBlockCategory;
        this.serviceProvider = serviceProvider;
        this.grids = [];
        this.globalInitializePromise = globalInitializePromise;
    }

    /**
     * Loads the mapping and initializes the grids in given container.
     * @param jQueryElement Container where the grids should be initialized in.
     * @param displayType: Display type for the mapping.
     * @since 4.1.0
     */
    public async initialize(container: JQuery, displayType: MappingDisplayType = MappingDisplayType.FilterAndGrids): Promise<void> {
        this.mappingDefinition = await this.serviceProvider.GridDefinition.getMapping(this.mappingName, displayType);
        this.container = container;

        if (window && (<any>$(window)).bindFirst) {
            (<any>$(window)).bindFirst("beforeunload", (e: any) => this.beforeUnloadUnsavedChangesHandler(e));
        }

        if (this.mappingDefinition.IsMappingGridConfig) {
            this.initializeUserGridConfig(container);
        }

        if (this.mappingDefinition.GridDefinitions && this.mappingDefinition.GridDefinitions.length > 0) {
            for (let i = 0, cnt = this.mappingDefinition.GridDefinitions.length; i < cnt; i++) {
                let gridDefinition = this.mappingDefinition.GridDefinitions[i];
                await this.initializeGrid(container, gridDefinition);
            }
            if (this.globalInitializePromise) {
                await this.globalInitializePromise;
            }
            // this.resize();
            this.initialLoad();
        } else {
            let mappingNotFound = require("../Grids/Templates/MappingNotFound.html").default;
            container.html(mappingNotFound);
            $(".dms-mapping-header").hide();
        }
    }

    private beforeUnloadUnsavedChangesHandler(e: any) {
        // CoreContext.Instance.allowPageTokenRelease = true;

        for (const grid of this.getGrids()) {
            if (grid.isChangeTrackingEnabled() && grid.hasChanges()) {
                // CoreContext.Instance.allowPageTokenRelease = false;
                let message = this.serviceProvider.TextBlock.get("DiscardUnsavedChangesMessage", this.textBlockCategory);
                e = e || window.event;

                // For IE and Firefox
                if (e) {
                    e.returnValue = message;
                }

                return message;
            }
        }
    }

    /**
     * Initially load the mapping.
     * @hidden
     */
    public async initialLoad(loadGrids: IBaseGrid[] = null): Promise<void> {
        const grids = loadGrids || this.grids;
        let hasFilter = false;
        for (const grid of grids) {
            this.attachGridEvents(grid);
            if (grid.getGridType() == GridTypes.Filter) {
                hasFilter = true;
                grid.one(GridEventTypes.ControlDataLoaded, (e) => this.onFilterControlDataLoaded());
                await grid.initialLoad();
            }
        }
        if (!hasFilter) {
            await this.onFilterControlDataLoaded();
        }
    }

    private attachGridEvents(grid: IBaseGrid) {
        if (grid.getGridType() == GridTypes.Filter) {
            grid.on(GridEventTypes.FilterCollapsed, (e) => this.resize());
            grid.on(GridEventTypes.FilterCollapsing, (e) => this.setGridHeights(250));
        }
        grid.on(GridEventTypes.ResizeRequested, (e) => this.resize());
        grid.on(GridEventTypes.ReinitializeGrid, (e) => this.onReinitializeGrid(e));
    }

    /**
     * Initialize grid in given wrapper.
     * @param jQueryElement The element in which the grid should be initialized.
     * @param gridDefinition The gridDefinition for the grid.
     * @param gridCollection (Optional) The gridCollection where the grid is added if given.
     * @hidden
     */
    private async initializeGrid(container: JQuery, gridDefinition: BaseGridDefinition): Promise<IBaseGrid> {
        let gridTypeConfiguration = Configuration.getGridTypeConfiguration(gridDefinition.GridTypeID);
        if (gridTypeConfiguration != null) {
            try {
                let template = kendo.template(gridTypeConfiguration.gridSettings.getTemplate(TemplateTypes.Wrapper), {
                    useWithBlock: false,
                });
                let gridTmpl = template(gridDefinition);
                let jQueryGrid = $(gridTmpl).appendTo(container);
                let wrapper = jQueryGrid.find(".dms-grid-wrapper");
                await this.initializeGridInContainer(wrapper, gridDefinition);
            } catch (ex) {
                console.log(ex);
            }
        }
        return null;
    }

    /**
     * Initialize grid in the given container.
     * @param container Container where the grid should be initialized in.
     * @param gridDefinition The definition of teh grid.
     */
    private async initializeGridInContainer(container: JQuery, gridDefinition: BaseGridDefinition): Promise<IBaseGrid> {
        let gridTypeConfiguration = Configuration.getGridTypeConfiguration(gridDefinition.GridTypeID);
        if (gridTypeConfiguration != null) {
            let grid = gridTypeConfiguration.factory(
                gridDefinition,
                this.textBlockCategory,
                gridTypeConfiguration.gridSettings,
                this.serviceProvider,
                this.environment
            );
            this.grids.push(grid);

            if (this.userGridConfigExtension) {
                let userGridConfig = this.userGridConfigExtension.getUserGridConfig(gridDefinition.GridID);
                if (userGridConfig) {
                    grid.setInitialUserGridConfig(userGridConfig);
                }
            }

            await grid.setElement(container);
            if (gridDefinition.GridHeight != null) {
                grid.setHeight(gridDefinition.GridHeight);
            }
            return grid;
        }
    }

    /**
     * Occurs after the filter control data has been loaded.
     */
    private async onFilterControlDataLoaded(): Promise<void> {
        for (let i = 0, cnt = this.grids.length; i < cnt; i++) {
            let grid = this.grids[i];
            if (grid.getGridType() != GridTypes.Filter) {
                await grid.initialLoad();
            }
        }

        if (QueryParameter.get("apply") == "1") {
            this.triggerDefaultButton();
        }
    }

    /**
     * Trigger the default button on this mapping.
     * @since 4.1.0
     */
    public triggerDefaultButton(): boolean {
        for (let i = 0, len = this.grids.length; i < len; i++) {
            if (this.grids[i].getEditContext().triggerDefaultButton()) {
                return true;
            }
        }
        return false;
    }

    /**
     * Resize the mapping to the current height.
     */
    private resize(): void {
        if (this.height === undefined) {
            this.height = AppContext.current.getAvailableHeight();
        }
        let gridCount = 0;
        let filterHeight = 0;
        if (this.grids) {
            for (let i = 0, len = this.grids.length; i < len; i++) {
                let grid = this.grids[i];

                if (grid.isVisible()) {
                    if (grid.isAutoResize) {
                        gridCount++;
                    } else {
                        filterHeight += $(grid.getElement()).parent(".dms-outer-element").outerHeight(true);
                    }
                }
            }

            if (gridCount > 0) {
                let availableGridHeight = this.height - filterHeight;

                let gridHeight = Math.floor(availableGridHeight > 150 ? availableGridHeight / gridCount - 2 : 200); // -2 for border on kendo Grid
                for (let i = 0, len = this.grids.length; i < len; i++) {
                    let grid = this.grids[i];
                    if (grid.isAutoResize && grid.isVisible()) {
                        grid.setHeight(gridHeight);
                    }
                }
            }
        }
    }

    /**
     * Set the height of the mapping.
     * @param height Height in pixel.
     * @since 4.1.0
     */
    public setHeight(height: number) {
        this.height = height;
        this.resize();
    }

    /**
     * Set the height of all grids which are on autoResize.
     * @param height Height for all grids which are autosized.
     * @hidden
     */
    private setGridHeights(height: number): void {
        for (let i = 0, len = this.grids.length; i < len; i++) {
            let grid = this.grids[i];
            if (grid.isAutoResize) {
                grid.setHeight(height); // -2 for border on kendo Grid
            }
        }
    }

    /**
     * Get the list of grids of this mapping.
     * @since 4.1.0
     */
    public getGrids(): IBaseGrid[] {
        return this.grids || [];
    } 

    /**
     * Get the grid with the given id of this mapping.
     * @param gridId Grid ID.
     * @since 4.1.0
     */
    public getGrid(gridId: number) {
        for (let i = 0, len = this.grids.length; i < len; i++) {
            if (this.grids[i].getGridId() == gridId) {
                return this.grids[i];
            }
        }
        return null;
    }

    /**
     * Occurs when the grid wants to be reinitialized.
     * @param e EventArgs.
     */
    private onReinitializeGrid(e: EventArgs<IBaseGrid>): void {
        let grid = e.sender;

        const gridId = grid.getGridId();

        this.serviceProvider.GridDefinition.getGrid(this.mappingName, gridId).then((gridDefinition) => {
            grid.dispose();
            let idx = this.grids.indexOf(grid);
            this.grids.splice(idx, 1);
            idx = this.grids.indexOf(grid);
            if (idx >= 0) {
                this.grids.splice(idx, 1);
            }

            let element = $(grid.getElement());
            element.empty();
            this.initializeGridInContainer(element, gridDefinition).then((newGrid) => {
                this.attachGridEvents(newGrid);
                newGrid.reload();
                this.resize();
            });
        });
    }

    /**
     * Reload all grids of this mapping.
     * @since 4.1.0
     */
    public async reload(): Promise<void> {
        for (let i = 0, len = this.grids.length; i < len; i++) {
            let grid = this.grids[i];
            if (grid.getGridType() != GridTypes.Filter) {
                await grid.reload();
            }
        }
    }

    /**
     * Show the busy indicator for this mapping.
     * @param isBusy Pass true to show, false to hide the busy indicator.
     * @since 4.1.0
     */
    public setBusy(isBusy: boolean) {
        kendo.ui.progress(this.container, isBusy);
    }

    /**
     * Get the mapping definition.
     * @since 4.1.0
     */
    public getMappingDefinition(): MappingDefinition {
        return this.mappingDefinition;
    }

    /**
     * Get the mapping name.
     * @since 4.1.0
     */
    public getMappingName(): string {
        return this.mappingName;
    }

    /**
     * Initialize the user grid config in the container.
     * @param container Container.
     * @hidden
     */
    protected async initializeUserGridConfig(container: JQuery): Promise<void> {
        if (!this.userGridConfigExtension) {
            this.userGridConfigExtension = new UserGridConfigExtension(
                this,
                this.serviceProvider.TextBlock,
                this.serviceProvider.GridDefinition,
                this.serviceProvider.UserGridConfig,
                this.environment
            );
        }
        await this.userGridConfigExtension.initialize(container);
    }

    /**
     * Get mapping container.
     * @hidden
     */
    public getElement(): HTMLElement {
        return this.container[0];
    }

    /**
     * Dispose the mapping.
     * @hidden
     */
    public dispose(): void {
        if (this.grids) {
            for (let i = 0, len = this.grids.length; i < len; i++) {
                this.grids[i].dispose();
            }
            this.grids = null;
        }
        if (this.userGridConfigExtension) {
            this.userGridConfigExtension.dispose();
        }
    }
}
